import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import * as THREE from "three";
import { WOOD_COLOR } from "./constants";

interface WheelProps {
  speedRef: React.MutableRefObject<number>;
  reducedMotion: boolean;
}

/**
 * The spinning wheel head + wooden pillar underneath the clay.
 * Rotates with the clay. Uses procedural grain via subtle noise mixed into base color.
 */
export function Wheel({ speedRef, reducedMotion }: WheelProps) {
  const headRef = useRef<THREE.Mesh>(null);

  useFrame((_state, deltaRaw) => {
    const delta = Math.min(deltaRaw, 0.1);
    if (!headRef.current) return;
    const rot = reducedMotion ? 0 : speedRef.current * 5;
    headRef.current.rotation.y += rot * delta;
  });

  return (
    <group position={[0, 0, 0]}>
      {/* Wheel head — the spinning wooden disk under the clay */}
      <mesh ref={headRef} position={[0, -0.15, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[3.2, 3.2, 0.3, 64]} />
        <meshStandardMaterial color={WOOD_COLOR} roughness={0.85} metalness={0.0} />
      </mesh>
      {/* Small metal collar */}
      <mesh position={[0, -0.32, 0]} receiveShadow>
        <cylinderGeometry args={[3.25, 3.35, 0.06, 64]} />
        <meshStandardMaterial color="#3A2E24" roughness={0.6} metalness={0.35} />
      </mesh>
      {/* Pillar base */}
      <mesh position={[0, -1.6, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.6, 2.0, 2.5, 48]} />
        <meshStandardMaterial color="#3E3227" roughness={0.9} metalness={0.0} />
      </mesh>
      {/* Table plane far below */}
      <mesh position={[0, -3.0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[9, 64]} />
        <meshStandardMaterial color="#2A221E" roughness={0.95} metalness={0.0} />
      </mesh>
    </group>
  );
}